import React, { useEffect } from 'react';
import clsx from 'clsx';
import { NameTextField } from '../../../styling/Styles';
import {
  checkExistDataElementName,
  checkIfEmpty,
  checkHasSpaces
} from '../../../validations/validations';
import AddRow from './AddRow';
import FormRow from './FormRow';

/**
 * @param {[][]} props.typeFields - 2d array which contains for every row of a form the types of the inside components  
 * @param {setState} props.setTypeFields - hook method that handle typeFields variable
 * @param {[][]} props.component - 2d array  which contains for every row of a form the inside components
 * @param {setState} props.setComponents - hook method that handle components variable 
 * @param {string} props.dataFormName - name of form data element
 * @param {setState} props.setDataFormName - hook method that handle dataFormName variable
 * @param {boolean} props.openAlert - state to display/hide form data element pop up
 * @param {number} props.selectedProject - index of user's selected project stored in local storage
 * @param {number} props.indexEditBlockDataElement - index of stored data element
 * @param {boolean} props.clickedEditBlockDataElement - state that shows if pop up has opened to edit an existing data element or to create one
 * @param {boolean} props.clickedGenerateBlock - state that form data element is going to be created or updated 
 * @param {setState} props.setClickedGenerateBlock - hook method that handle clickedGenerateBlock variable
 * @param {Object} props.editor - grapesJs editor
 * @param {method} props.savedPageClicked - save current project's page
 * @returns form data element inside data element pop up
 */
const FormDataElement = (props) => {
  const {
    typeFields,
    setTypeFields,
    components,
    setComponents,
    dataFormName,
    setDataFormName,
    openAlert,
    selectedProject,
    indexEditBlockDataElement,
    clickedEditBlockDataElement,
    clickedGenerateBlock,
    setClickedGenerateBlock,
    editor,
    savedPageClicked
  } = props;

  const [init, setInit] = React.useState(false); // init variable in order to initialize form data element 
  const [clickedChangeElement, setClickedChangeElement] = React.useState(false); // variable that is used to render again form data element
  const [finisProductDataElement, setFinishProductDataElement] = React.useState(false); // variable that is set to true after data element is created in order to save page

  /**
   * Initialize data form name 
   */
  useEffect(() => {
    if (init === false || openAlert === true || clickedEditBlockDataElement !== true) {
      setInit(true);
      setDataFormName('formName');
      setFinishProductDataElement(false);
    }
  }, [init, openAlert]);

  /**
   * @param {string} pageName - name of current page 
   * @returns id of given page name
   */
  const findId = (pageName) => {
    const tempData = JSON.parse(localStorage.getItem('data'))[selectedProject].pages;

    let id = -1;

    tempData.forEach((page) => {
      if (page.name === pageName) {
        id = page.pageId;
      }
    });

    return id;
  };

  // Save page after data element has been created or updated
  useEffect( () => {

    if(finisProductDataElement === true){
      savedPageClicked();
    }

  },[finisProductDataElement])

  // Create data element or update data element after clickedGenerateBlock has been set to true
  useEffect(() => {
    if (clickedGenerateBlock === true) {
      // Check if data element name is empty or has spaces or already exists
      if (
        checkIfEmpty(dataFormName) === false &&
        checkHasSpaces(dataFormName) === false &&
        checkExistDataElementName(dataFormName, selectedProject, indexEditBlockDataElement) ===
          false
      ) {
        const tempComponents = [];

        // Create object to use it inside grapesJs editor block manager to create new block
        typeFields.forEach((field, index) => {
          if (tempComponents[index] === undefined) {
            tempComponents.push({
              components: [],
              classes: ['form-group','flex']
            });
          }
          field.forEach((insideComp, index2) => {
            const comp = components[index][index2];

            if (insideComp === 'input') {
              tempComponents[index].components.push({
                type: 'input',
                attributes: {
                  type: comp.inputType,
                  placeholder: comp.placeholder,
                  required: comp.required,
                  name: '-1'
                },
                classes: ['form-control']
              });
            } else if (insideComp === 'label') {
              tempComponents[index].components.push({
                type: 'label',
                components: comp.labelName,
                classes: ['col-form-label']
              });
            } else if (insideComp === 'checkbox') {
              tempComponents[index].components.push({
                type: 'checkbox',
                attributes: { checked: comp.checked },
                
              });
            } else if (insideComp === 'radio') {
              comp.labels.forEach((label) => {
                tempComponents[index].components.push({
                  tagName: 'div',
                  attributes: { wablitype: 'radios' },
                  classes: ['form-check', 'form-check-inline'],
                  components: [
                    {
                      tagName: 'input',
                      type: 'radio',
                      attributes: { checked: true, value: label },
                      classes: ['form-check-input']
                    },
                    {
                      type: 'label',
                      classes: ['form-check-label'],
                      components: label
                    }
                  ]
                });
              });
            } else if (insideComp === 'button') {
              const targetPageId = findId(comp.target_page);

              tempComponents[index].components.push({
                type: 'button',
                components: comp.label,
                attributes: { target_page: targetPageId, href: comp.href, method: comp.method },
                classes: ['btn', 'btn-primary']
              });
            } else if (insideComp === 'textarea') {
              tempComponents[index].components.push({
                type: 'textarea',
                attributes: { placeholder: comp.placeholder, name: '-1', required: comp.required },
                classes: ['form-control']
              });
            } else if (insideComp === 'dropdown') {
              const tempOptionsComponents = [];
              comp.options.forEach((option) => {
                tempOptionsComponents.push({
                  tagName: 'li',
                  components: [
                    {
                      tagName: 'a',
                      type: 'link',
                      components: option,
                      classes: ['dropdown-item']
                    }
                  ]
                });
              });

              tempComponents[index].components.push({
                tagName: 'span',
                attributes: { wablitype: 'dropdown' },
                classes: ['dropdown'],
                components: [
                  {
                    type: 'button',
                    attributes: {
                      'aria-expanded': false,
                      'data-bs-toggle': 'dropdown',
                      id: 'dropdownMenuButton1-2',
                      wablitype: 'button',
                      wabliname: 'button'
                    },
                    classes: ['btn', 'dropdown-toggle'],
                    components: comp.label
                  },
                  {
                    tagName: 'ul',
                    attributes: {
                      'aria-labelledby': 'dropdownMenuButton1'
                    },
                    classes: ['dropdown-menu'],
                    components: tempOptionsComponents
                  }
                ]
              });
            }
          });
        });

        // Add block inside grapesJs 
        editor.BlockManager.add(dataFormName, {
          label: `${dataFormName}Block`,
          content: {
            components: tempComponents,
            type: 'form',
            tagName: 'form',
            attributes: {
              wabliname: dataFormName,
              wablitype: 'form'
            }
          },
          category: {
            label: 'Extra ',
            open: 'false'
          }
        });

        const tempData = JSON.parse(localStorage.getItem('data'));
        const tempDataElementBlock = {
          id: dataFormName,
          content: {
            components: tempComponents,
            type: 'form',
            tagName: 'form',
            attributes: {
              wabliname: dataFormName,
              wablitype: 'form'
            }
          },
          label: `${dataFormName  } Block`,
          category: {
            label: 'Extra ',
            open: 'false'
          }
        };

        const tempDataElementInfo = {
          typeFields,
          dataName: dataFormName,
          components,
          type: 'form'
        };
        
        // Check to create or edit data element
        if (clickedEditBlockDataElement === true) {
          tempData[selectedProject].data_elements.data_elements_blocks[
            indexEditBlockDataElement
          ].blocks[0] = tempDataElementBlock;
          tempData[selectedProject].data_elements.data_elements_info[
            indexEditBlockDataElement
          ] = tempDataElementInfo;
        } else {
          tempData[selectedProject].data_elements.data_elements_blocks.push({
            blocks: [tempDataElementBlock]
          });
          tempData[selectedProject].data_elements.data_elements_info.push(tempDataElementInfo);
        }
        localStorage.setItem('data', JSON.stringify(tempData));
      }
      setClickedGenerateBlock(false);
      setFinishProductDataElement(true);
    }
  }, [clickedGenerateBlock]);

  // Edit form data element
  const handleDataNameInput = (val) => {
    setDataFormName(val.target.value);
  };
 
  useEffect(() => {
    if (clickedChangeElement === true) {
      setClickedChangeElement(false);
    }
  }, [clickedChangeElement]);

  const returnHelperText = () => {
    if(checkIfEmpty(dataFormName) === true){
      return 'Form name empty';
    }
    if(checkHasSpaces(dataFormName) === true){
      return 'Spaces are not allowed';
    }
    if (checkExistDataElementName(dataFormName,selectedProject,indexEditBlockDataElement) === true){
      return 'Name already exists';
    }
    return '';
    
  }

  return (
      <div style={{ marginTop: '1vw' }}>
        <div className={clsx('flexRowDirection')}>
          <span>Name of form: &nbsp;</span>
          {clickedEditBlockDataElement !== true ? (
            <NameTextField
              id="DataFormName"
              placeholder=" Name of form data"
              onInput={(e) => handleDataNameInput(e)}
              value={dataFormName}
              error={
                checkIfEmpty(dataFormName) === true ||
                checkHasSpaces(dataFormName) === true ||
                checkExistDataElementName(
                  dataFormName,
                  selectedProject,
                  indexEditBlockDataElement
                ) === true
              }
              helperText={ returnHelperText()}
             />
          ) : (
            <span>{dataFormName} </span>
          )}
        </div>
        <div style={{ marginTop: '1vw' }}>
          {components.map((componentsRow, index) => (
            <FormRow
              typeFields={typeFields}
              setTypeFields={setTypeFields}
              components={components}
              setComponents={setComponents}
              formRowIndex={index}
              clickedChangeElement={clickedChangeElement}
              setClickedChangeElement={setClickedChangeElement}
              openAlert={openAlert}
            />
          ))}
          <AddRow
            components={components}
            setComponents={setComponents}
            typeFields={typeFields}
            setTypeFields={setTypeFields}
          />
        </div>
      </div>
  );
};

export default FormDataElement;
